/**
 * useInstallPrompt Composable
 *
 * Handles PWA installation prompt for supported browsers
 * and provides iOS detection for manual install instructions
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Browser-specific APIs not in standard typings
interface NavigatorStandalone extends Navigator {
  standalone?: boolean
}

interface WindowWithMSStream extends Window {
  MSStream?: unknown
}

// Shared state across all instances
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const isInstalled = ref(false)
const bannerDismissed = ref(false)

// Check localStorage for dismissed state
const BANNER_DISMISSED_KEY = 'pwa-install-banner-dismissed'

export function useInstallPrompt() {
  /**
   * Detect iOS (Safari doesn't support beforeinstallprompt)
   */
  const isIOS = computed(() => {
    if (typeof navigator === 'undefined') return false
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as WindowWithMSStream).MSStream
  })

  /**
   * Detect if running in standalone mode (already installed)
   */
  const isStandalone = computed(() => {
    if (typeof window === 'undefined') return false
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      (window.navigator as NavigatorStandalone).standalone === true
    )
  })

  /**
   * Can show native install prompt (Chrome, Edge on desktop/Android)
   */
  const canInstall = computed(() => {
    return deferredPrompt.value !== null && !isInstalled.value
  })

  /**
   * Should show install UI (either native prompt available or iOS instructions)
   */
  const shouldShowInstallUI = computed(() => {
    return !isInstalled.value && !isStandalone.value && (canInstall.value || isIOS.value)
  })

  /**
   * Should show the banner (not dismissed, not installed)
   */
  const shouldShowBanner = computed(() => {
    return shouldShowInstallUI.value && !bannerDismissed.value
  })

  /**
   * Trigger the native install prompt
   */
  async function installApp(): Promise<boolean> {
    if (!deferredPrompt.value) {
      return false
    }

    try {
      await deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice

      if (outcome === 'accepted') {
        isInstalled.value = true
        deferredPrompt.value = null
        return true
      }
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error)
    }

    return false
  }

  /**
   * Dismiss the install banner
   */
  function dismissBanner(): void {
    bannerDismissed.value = true
    try {
      localStorage.setItem(BANNER_DISMISSED_KEY, 'true')
    } catch {
      // localStorage might not be available
    }
  }

  /**
   * Reset banner dismissed state (e.g., for testing or after some time)
   */
  function resetBanner(): void {
    bannerDismissed.value = false
    try {
      localStorage.removeItem(BANNER_DISMISSED_KEY)
    } catch {
      // localStorage might not be available
    }
  }

  // Event handlers
  function handleBeforeInstallPrompt(event: Event): void {
    event.preventDefault()
    deferredPrompt.value = event as BeforeInstallPromptEvent
  }

  function handleAppInstalled(): void {
    isInstalled.value = true
    deferredPrompt.value = null
  }

  onMounted(() => {
    // Check if banner was previously dismissed
    try {
      bannerDismissed.value = localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
    } catch {
      // localStorage might not be available
    }

    // Check if already in standalone mode
    if (isStandalone.value) {
      isInstalled.value = true
    }

    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
  })

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
  })

  return {
    // State
    isIOS,
    isStandalone,
    canInstall,
    shouldShowInstallUI,
    shouldShowBanner,
    isInstalled,

    // Actions
    installApp,
    dismissBanner,
    resetBanner,
  }
}
